"use client"

import { useEffect, useState } from "react"
import api from "../api/api"
import SearchTable from "./SearchTable"
import LoadingSpinner from "../utils/LoadingSpinner"
import { useNavigate, useLocation } from "react-router-dom"
import {
  Undo2,
  Gift,
  Factory,
  Gem,
  Truck,
  Zap,
  Wifi,
  BadgeDollarSign,
  HomeIcon as House,
  MonitorPlayIcon as TvMinimalPlay,
  Pill,
  ChevronRight,
  ArrowRight,
  ChevronDown,
} from "lucide-react"
import TwoColumnSwitch from "../utils/TwoColumnSwitch"

const IndustrySearch = ({
  onDashboardClick,
  selectedCompanies,
  onCompanyClick,
  industryData,
  addAllCompanies,
  registeredCompanies,
  setCompanyDescription,
  handleOpenAnalysis,
  isMobile,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [companies, setCompanies] = useState(null)
  const [groupedData, setGroupedData] = useState(null)
  const [showSearchTable, setShowSearchTable] = useState(false)
  const [selectedIndustry, setSelectedIndustry] = useState(null)
  const [showSpinner, setShowSpinner] = useState(false)
  const [expandedCategories, setExpandedCategories] = useState({})

  const getIcon = (industryName) => {
    switch (industryName) {
      case "消費財・小売":
        return <Gift />
      case "産業・工業":
        return <Factory />
      case "素材・鉱業・農業":
        return <Gem />
      case "運輸・物流":
        return <Truck />
      case "エネルギー・公益事業":
        return <Zap />
      case "テクノロジー・通信":
        return <Wifi />
      case "金融・保険":
        return <BadgeDollarSign />
      case "製薬・医療":
        return <Pill />
      case "サービス・メディア・その他":
        return <TvMinimalPlay />
      case "不動産・REIT・住宅":
        return <House />
      default:
        return <Gift />
    }
  }

  useEffect(() => {
    if (industryData) {
      const groupedData = industryData.reduce((acc, industry) => {
        // 最上位階層の名前を取得（デフォルトは"その他"）
        const class1Name = industry.industry_class_1_name || "その他"
        // 次の階層の名前を取得（デフォルトは"その他"）
        const class2Name = industry.industry_class_2_name || "その他"

        // 最上位階層が存在しない場合は初期化
        if (!acc[class1Name]) {
          acc[class1Name] = {}
        }

        // 次の階層が存在しない場合は初期化
        if (!acc[class1Name][class2Name]) {
          acc[class1Name][class2Name] = []
        }

        // データを追加
        acc[class1Name][class2Name].push(industry)
        return acc
      }, {})
      setGroupedData(groupedData)
    }
  }, [industryData])

  const onIndustryClick = (industry) => {
    const fetchIndustryCompanies = async () => {
      try {
        setShowSpinner(true)
        const response = await api.get(
          `${process.env.REACT_APP_API_URL}/chat/api/search-companies-by-industry/${industry.id}/`,
        )
        setCompanies(response.data)
        setSelectedIndustry(industry.industry_name)
        setShowSpinner(false)
        setShowSearchTable(true)
      } catch (error) {
        if (error.response && (error.response.status === 401 || error.response.status === 403)) {
          navigate("/login", { state: { from: location.pathname } })
        } else {
          console.error("Error fetching financial results:", error)
        }
      }
    }
    fetchIndustryCompanies()
  }

  const countryOptions = [
    { id: "JP", label: "日本" },
    { id: "US", label: "米国" },
  ]

  const [selectedCountry, setSelectedCountry] = useState("JP")
  const [filteredCompanies, setFilteredCompanies] = useState(null)

  useEffect(() => {
    if (companies) {
      const filteredCompanies = companies.filter((company) => company.country === selectedCountry)
      setFilteredCompanies(filteredCompanies)
    }
  }, [companies, selectedCountry])

  const onCountryChange = (country) => {
    setSelectedCountry(country)
  }

  // カテゴリの展開/折りたたみを切り替える
  const toggleCategory = (categoryName) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryName]: !prev[categoryName],
    }))
  }

  // サブカテゴリの展開/折りたたみを切り替える
  const toggleSubCategory = (categoryName, subCategoryName) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [`${categoryName}-${subCategoryName}`]: !prev[`${categoryName}-${subCategoryName}`],
    }))
  }

  // モバイル用の業界カテゴリ表示
  const renderMobileIndustryCategories = () => (
    <div className="px-3 pb-3">
      {Object.entries(groupedData).map(([class1Name, subClasses]) => (
        <div key={class1Name} className="mb-4">
          <button
            onClick={() => toggleCategory(class1Name)}
            className="w-full flex items-center justify-between bg-white p-3 rounded-lg shadow-sm border border-gray-100"
          >
            <div className="flex items-center">
              <span className="mr-4 text-emerald-500">{getIcon(class1Name)}</span>
              <span className="font-medium">{class1Name}</span>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-gray-400 transition-transform ${
                expandedCategories[class1Name] ? "rotate-180" : ""
              }`}
            />
          </button>

          {expandedCategories[class1Name] && (
            <div className="mt-2 ml-2">
              {Object.entries(subClasses).map(([class2Name, industries]) => (
                <div key={class2Name} className="mb-3">
                  <button
                    onClick={() => toggleSubCategory(class1Name, class2Name)}
                    className="w-full flex items-center justify-between bg-white p-2 rounded-md border border-gray-100"
                  >
                    <span className="font-medium text-sm">{class2Name}</span>
                    <ChevronDown
                      className={`w-4 h-4 text-gray-400 transition-transform ${
                        expandedCategories[`${class1Name}-${class2Name}`] ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  {expandedCategories[`${class1Name}-${class2Name}`] && (
                    <ul className="mt-1 ml-2 space-y-1">
                      {industries.map((industry) => (
                        <li key={industry.id}>
                          <button
                            onClick={() => onIndustryClick(industry)}
                            className="w-full text-left p-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md flex items-center justify-between"
                          >
                            <span>{industry.industry_name}</span>
                            <ChevronRight className="w-4 h-4 text-gray-400" />
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )

  // デスクトップ用の業界カテゴリ表示（既存のスタイルを維持）
  const renderDesktopIndustryCategories = () => (
    <div id="industry-container">
      {Object.entries(groupedData).map(([class1Name, subClasses]) => (
        <div className="class-group" key={class1Name}>
          <div className="">
            <div className="bold flex">
              {getIcon(class1Name)}
              <div className="ml-2 text-lg">{class1Name}</div>
            </div>
            <ul className="group-sub p-2">
              {Object.entries(subClasses).map(([class2Name, industries]) => (
                <li key={class2Name} className="sub-class-group">
                  <div className="text-md bold">{class2Name}</div>
                  <ul className="p-2 mb-4">
                    {industries.map((industry) => (
                      <li
                        key={industry.id}
                        className="normal cursor-pointer py-[2px] hover:underline"
                        onClick={() => onIndustryClick(industry)}
                      >
                        {industry.industry_name}
                      </li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ))}
    </div>
  )

  // モバイル用の検索結果ヘッダー
  const renderMobileResultHeader = () => (
    <div className="px-4 py-2 flex items-center">
      <div className="font-bold flex-grow">「{selectedIndustry}」の一覧</div>
      <button onClick={() => setShowSearchTable(false)} className="mr-2 p-1 rounded-full hover:bg-gray-100">
        <Undo2 className="h-4 w-4 text-gray-600" />
      </button>
      {selectedCompanies && selectedCompanies.length > 0 && (
        <button
          onClick={handleOpenAnalysis}
          className="flex items-center gap-1.5 bg-gray-800 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-gray-900 transition-colors"
        >
          分析を表示
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  )

  // デスクトップ用の検索結果ヘッダー（既存のスタイルを維持）
  const renderDesktopResultHeader = () => (
    <div className="ml-4 flex flex-wrap gap-4">
      <div id="industryCompaniesTitle">「{selectedIndustry}」の一覧</div>
      <div
        className="ml-4 rounded-md hover:bg-gray-400 hover:text-white px-1 flex items-center justify-center"
        onClick={() => setShowSearchTable(false)}
      >
        <Undo2 className="h-4 w-4" />
      </div>
      {selectedCompanies && selectedCompanies.length > 0 && (
        <div
          className="show-analysis-sm p-1 bg-green-500 w-[120px] flex align-center items-center text-white rounded-2xl cursor-pointer"
          onClick={handleOpenAnalysis}
        >
          分析画面を開く
        </div>
      )}
    </div>
  )

  return (
    <div className={isMobile ? "" : "tabcontent-search"}>
      <div className={isMobile ? "" : "analysis-container !overflow-y-auto"}>
        {/* 国別フィルター */}
        <div className={isMobile ? "px-3 py-2 sticky top-0 z-10" : "flex px-[10px] my-2 gap-3"}>
          <TwoColumnSwitch
            options={countryOptions}
            onChange={onCountryChange}
            defaultId={selectedCountry}
            height={6}
            textSize="text-xs"
            isMobile={isMobile}
          />
        </div>

        {showSearchTable ? (
          <>
            {/* 検索結果ヘッダー */}
            {isMobile ? (
              <div className="sticky top-10 z-10">{renderMobileResultHeader()}</div>
            ) : (
              renderDesktopResultHeader()
            )}

            {/* ローディングスピナー */}
            {showSpinner ? (
              <div className={isMobile ? "flex justify-center py-8" : "modal-background"}>
                <LoadingSpinner />
              </div>
            ) : (
              filteredCompanies && (
                <SearchTable
                  searchResults={filteredCompanies}
                  onDashboardClick={onDashboardClick}
                  selectedCompanies={selectedCompanies}
                  onCompanyClick={onCompanyClick}
                  addAllCompanies={addAllCompanies}
                  registeredCompanies={registeredCompanies}
                  setCompanyDescription={setCompanyDescription}
                  isMobile={isMobile}
                />
              )
            )}
          </>
        ) : (
          <div id="industry-search-container" className={isMobile ? "" : "main-contents-container overflow-auto"}>
            {showSpinner || !groupedData ? (
              <div className={isMobile ? "flex justify-center py-8" : "modal-background"}>
                <LoadingSpinner />
              </div>
            ) : isMobile ? (
              renderMobileIndustryCategories()
            ) : (
              renderDesktopIndustryCategories()
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default IndustrySearch
